import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import NotificationBellButton from '../../components/NotificationBellButton';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const formatEuro = (value) => `${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(value || 0))}€`;
const formatDate = (value) => new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value));

export default function HostTipHistoryPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTips = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/host/tips/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTips(response.data.tips || []);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Unable to load tip history right now');
      } finally {
        setLoading(false);
      }
    };

    fetchTips();
  }, []);

  return (
    <div className="app-shell" data-testid="host-tip-history-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={goBack} data-testid="host-tip-history-back-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={30} color="var(--text)" strokeWidth={1.7} />
        </button>
        <NotificationBellButton buttonTestId="host-tip-history-bell-button" popupTestId="host-tip-history-notification-popup" />
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, paddingTop: 0 }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 118, height: 102, objectFit: 'contain' }} />
        <h1 style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '2px 0 4px', textAlign: 'center' }}>Tipping History</h1>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <div style={{ ...hostCardStyle, padding: '16px 18px', textAlign: 'center' }}>
              <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', margin: 0 }}>Loading tip history…</p>
            </div>
          ) : tips.length === 0 ? (
            <div style={{ ...hostCardStyle, padding: '16px 18px', textAlign: 'center' }}>
              <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', margin: 0 }}>No tips yet</p>
            </div>
          ) : tips.map((tip) => (
            <div key={tip.id} data-testid={`host-tip-history-item-${tip.id}`} style={{ ...hostCardStyle, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{tip.guest_name}</p>
                  <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '6px 0 0' }}>Tipped on {formatDate(tip.tipped_at)}</p>
                </div>
                <p style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: '#04A628', margin: 0 }}>{formatEuro(tip.amount)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}